import React, { useEffect, useState } from 'react'
import {
    Grid,
    Box,
    Paper,
    CircularProgress,
    Typography,
    Chip,
    Fade,
    IconButton,
    Divider,
    Link
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { useParams, useHistory } from "react-router-dom"
import * as actions from "./actions"
import { toastError } from '../../utils';
import moment from 'moment';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SEO from '../../components/SEO';

const PageContainer = styled(Box)({
    minHeight: 'calc(100vh - 64px)',
    background: 'linear-gradient(135deg, #e8eef3 0%, #dce4ec 100%)',
    padding: '32px 24px',
});

const PageHeader = styled(Box)({
    marginBottom: '24px',
    '& h1': {
        fontSize: '1.75rem',
        fontWeight: 700,
        background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        fontFamily: "'DM Sans', sans-serif",
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
});

const InfoCard = styled(Paper)({
    borderRadius: '16px',
    padding: '24px',
    background: 'white',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    marginBottom: '24px',
});

const InfoItem = styled(Box)({
    '& .label': {
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#5f6368',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '6px',
    },
    '& .value': {
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '1rem',
        fontWeight: 600,
        color: '#202124',
    },
});

const ConcernCard = styled(Paper)({
    borderRadius: '16px',
    padding: '24px',
    background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
    border: '2px solid #bbdefb',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    marginBottom: '24px',
});

const ConversationCard = styled(Paper)({
    borderRadius: '16px',
    padding: '24px',
    background: 'white',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    minHeight: '400px',
    display: 'flex',
    flexDirection: 'column',
});

const MessageBubble = styled(Box)(({ isuser }) => ({
    padding: '12px 16px',
    borderRadius: '12px',
    marginBottom: '16px',
    maxWidth: '75%',
    width: 'fit-content',
    clear: 'both',
    position: 'relative',
    ...(isuser ? {
        marginLeft: 'auto',
        background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
        color: 'white',
        borderBottomRightRadius: '4px',
    } : {
        marginRight: 'auto',
        background: '#f5f5f5',
        color: '#202124',
        borderBottomLeftRadius: '4px',
    }),
}));

const MessageHeader = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.8125rem',
    fontWeight: 600,
});

const MessageText = styled(Typography)({
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9375rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    lineHeight: 1.5,
});

const MessageTime = styled(Typography)(({ isuser }) => ({
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.6875rem',
    marginTop: '6px',
    opacity: 0.8,
    textAlign: isuser ? 'right' : 'left',
}));

const ReplyInputContainer = styled(Box)({
    display: 'flex',
    gap: '12px',
    marginTop: 'auto',
    paddingTop: '16px',
    borderTop: '2px solid #f0f0f0',
});

const ReplyTextarea = styled('textarea')({
    flex: 1,
    padding: '12px 16px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9375rem',
    borderRadius: '12px',
    border: '2px solid #e0e0e0',
    backgroundColor: '#f8f9fa',
    color: '#202124',
    resize: 'none',
    minHeight: '56px',
    maxHeight: '120px',
    transition: 'all 0.3s ease',
    '&:hover': {
        borderColor: '#1a73e8',
        backgroundColor: '#ffffff',
    },
    '&:focus': {
        outline: 'none',
        borderColor: '#1a73e8',
        backgroundColor: '#ffffff',
        boxShadow: '0 0 0 3px rgba(26, 115, 232, 0.1)',
    },
    '&::placeholder': {
        color: '#9e9e9e',
    },
});

const SendButton = styled(IconButton)({
    width: '56px',
    height: '56px',
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    color: 'white',
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'linear-gradient(135deg, #1557b0 0%, #00a589 100%)',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 16px rgba(26, 115, 232, 0.4)',
    },
    '&:disabled': {
        background: '#e0e0e0',
        color: '#9e9e9e',
    },
});

const BackButton = styled(IconButton)({
    color: '#1a73e8',
    '&:hover': {
        backgroundColor: '#e3f2fd',
    },
});

const StatusChip = styled(Chip)(({ statustype }) => ({
    fontWeight: 600,
    fontSize: '0.75rem',
    height: '28px',
    fontFamily: "'DM Sans', sans-serif",
    ...(statustype === 'done' ? {
        backgroundColor: '#4caf50',
        color: '#ffffff',
    } : {
        backgroundColor: '#ff9800',
        color: '#ffffff',
    }),
}));

// Helper function to detect and linkify URLs
const linkifyText = (text, isUserMessage = false) => {
    if (!text) return null;

    // Regular expression to detect URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
        if (part.match(urlRegex)) {
            return (
                <Link
                    key={index}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        color: isUserMessage ? '#ffffff' : '#1a73e8',
                        textDecoration: 'underline',
                        fontWeight: 600,
                        '&:hover': {
                            color: isUserMessage ? '#e3f2fd' : '#1557b0',
                        }
                    }}
                >
                    {part}
                </Link>
            );
        }
        return part;
    });
};

function ViewTicket(props) {
    const params = useParams();
    const history = useHistory();

    const [ticket, setTicket] = useState({})
    const [sendReply, setSendReply] = useState({
        ticket_id: params.id,
        reply: '',
        attachement: "",
        sent_by: "user"
    })
    const [sendLoader, setSendLoader] = useState(false)

    useEffect(() => {
        if (params.id) {
            fetchTicketsById()
        }
    }, [])

    const fetchTicketsById = () => {
        props.setLoader(true)
        actions.getTicketById(params.id).then(res => {
            props.setLoader(false)
            setTicket({ ...res.body, attachement: (res.body.attachement && res.body.attachement.length) ? res.body.attachement.split(',') : [res.body.attachement] })
        }).catch(err => {
            props.setLoader(false)
            toastError(err.message)
        })
    }

    const onSubmitReply = () => {
        if (!sendReply.reply) return toastError('Please enter a reply first.')
        setSendLoader(true)
        actions.replyTicket(sendReply).then(res => {
            setSendLoader(false)
            setSendReply({ ...sendReply, reply: "" })
            fetchTicketsById()
        }).catch(err => {
            setSendLoader(false)
            toastError(err.message)
        })
    }

    const keyPressed = e => {
        if (e.key === 'Enter' && e.shiftKey) {
            return
        }
        if (e.key === "Enter") {
            e.preventDefault();
            onSubmitReply()
        }
    }

    const { reason = '', date = '', reply = '', attachement = [], sent_by = '', status, replies = [], created_at = '' } = ticket || {}

    return (
        <PageContainer>
            <SEO
                title={reason ? `${reason} - Ticket Details` : 'Ticket Details'}
                description={`View and manage support ticket details including messages and status updates.`}
            />
            <Fade in timeout={800}>
                <Box>
                    {/* Page Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <BackButton onClick={() => history.push('/tickets')}>
                            <ArrowBackIcon />
                        </BackButton>
                        <PageHeader sx={{ mb: 0 }}>
                            <Typography variant="h1">
                                <ConfirmationNumberIcon sx={{ fontSize: 32, color: '#1a73e8' }} />
                                Ticket Details
                            </Typography>
                        </PageHeader>
                    </Box>

                    {/* Ticket Info Card */}
                    <InfoCard>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <InfoItem>
                                    <Typography className="label">Ticket ID</Typography>
                                    <Typography className="value">#{params.id}</Typography>
                                </InfoItem>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <InfoItem>
                                    <Typography className="label">Reason</Typography>
                                    <Typography className="value">{reason}</Typography>
                                </InfoItem>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <InfoItem>
                                    <Typography className="label">Created Date</Typography>
                                    <Typography className="value">
                                        {moment(date).format('DD/MM/YYYY')}
                                    </Typography>
                                </InfoItem>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <InfoItem>
                                    <Typography className="label">Status</Typography>
                                    <StatusChip
                                        statustype={status === 0 ? 'pending' : 'done'}
                                        label={status === 0 ? 'OPEN' : 'RESOLVED'}
                                        icon={status === 0 ? <PendingIcon /> : <CheckCircleIcon />}
                                        size="small"
                                    />
                                </InfoItem>
                            </Grid>
                        </Grid>
                    </InfoCard>

                    {/* Your Concern Card */}
                    <ConcernCard>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: '#1a73e8',
                                    mb: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <PersonIcon sx={{ fontSize: 18 }} />
                                    Your Concern
                                </Typography>
                                <Typography sx={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '1rem',
                                    color: '#202124',
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap'
                                }}>
                                    {linkifyText(reply)}
                                </Typography>
                            </Box>
                            <Box sx={{ minWidth: '150px' }}>
                                <Typography sx={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.75rem',
                                    color: '#5f6368',
                                    mb: 0.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                }}>
                                    <AccessTimeIcon sx={{ fontSize: 14 }} />
                                    Created On
                                </Typography>
                                <Typography sx={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: '#202124',
                                }}>
                                    {moment(created_at).format('DD/MM/YYYY hh:mm A')}
                                </Typography>
                            </Box>
                        </Box>
                    </ConcernCard>

                    {/* Conversation Card */}
                    <ConversationCard>
                        <Typography sx={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: '1.125rem',
                            fontWeight: 700,
                            color: '#202124',
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            Conversation
                        </Typography>

                        <Divider sx={{ mb: 3 }} />

                        {/* Messages */}
                        <Box sx={{ flex: 1, overflowY: 'auto', mb: 2 }}>
                            {replies && replies.length ? replies.map((item, key) => {
                                const isUser = item.sent_by === sent_by;
                                return (
                                    <MessageBubble key={key} isuser={isUser ? 1 : 0}>
                                        <MessageHeader style={{ color: isUser ? 'rgba(255,255,255,0.9)' : '#5f6368' }}>
                                            {isUser ? (
                                                <>
                                                    <PersonIcon sx={{ fontSize: 16 }} />
                                                    You
                                                </>
                                            ) : (
                                                <>
                                                    <SupportAgentIcon sx={{ fontSize: 16 }} />
                                                    Support Team
                                                </>
                                            )}
                                        </MessageHeader>
                                        <MessageText style={{ color: isUser ? 'white' : '#202124' }}>
                                            {linkifyText(item.reply, isUser)}
                                        </MessageText>
                                        <MessageTime isuser={isUser ? 1 : 0}>
                                            {moment(item.created_at).format('DD/MM/YYYY hh:mm A')}
                                        </MessageTime>
                                    </MessageBubble>
                                );
                            }) : (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 8,
                                    color: '#9e9e9e'
                                }}>
                                    <SupportAgentIcon sx={{ fontSize: 60, mb: 2, color: '#e0e0e0' }} />
                                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 600 }}>
                                        No replies yet
                                    </Typography>
                                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', mt: 0.5 }}>
                                        Our support team will respond soon
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Reply Input */}
                        {status === 1 ? (
                            <Box sx={{
                                p: 2,
                                borderRadius: '12px',
                                backgroundColor: '#f5f5f5',
                                textAlign: 'center'
                            }}>
                                <Typography sx={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.875rem',
                                    color: '#5f6368',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1
                                }}>
                                    <CheckCircleIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                                    This ticket has been closed
                                </Typography>
                            </Box>
                        ) : (
                            <ReplyInputContainer>
                                <ReplyTextarea
                                    placeholder="Type your reply... (Press Enter to send, Shift+Enter for new line)"
                                    value={sendReply.reply}
                                    onKeyPress={keyPressed}
                                    onChange={(e) => setSendReply({ ...sendReply, reply: e.target.value })}
                                    rows={2}
                                />
                                <SendButton onClick={onSubmitReply} disabled={sendLoader || !sendReply.reply.trim()}>
                                    {sendLoader ? (
                                        <CircularProgress size={24} style={{ color: 'white' }} />
                                    ) : (
                                        <SendIcon />
                                    )}
                                </SendButton>
                            </ReplyInputContainer>
                        )}
                    </ConversationCard>
                </Box>
            </Fade>
        </PageContainer>
    )
}

export default ViewTicket
